import { CategoryDto, CategoryModel } from 'api/dto/Category.dto';
import { mapPageDtoToModel, PageDto, PageModel } from 'api/dto/Page.dto';
import { PageItemModel } from 'api/dto/PageItem.dto';
import { ProjectKind } from 'model/ProjectKind';
import dayjs, { Dayjs } from 'dayjs';

export type ProjectTemplatePostDto = {
  name: string;
  kind: ProjectKind;
};

export type ProjectTemplateDto = {
  categories?: CategoryDto[];
  createdAt: string;
  createdBy: string;
  description?: string | null;
  id: number;
  kind: ProjectKind;
  name: string;
  pages?: PageDto[];
  updatedAt: string;
  updatedBy: string;
};

export type TemplateImportPreviewQuestionDto = {
  row: number;
  text: string | null;
  type: string | null;
  category: string | null;
  scale: string | null;
  maxAnswers: number | null;
  questionReference: string | null;
  questionLibraryReference: string | null;
  logic: string | null;
  options: (string | null)[];
};

export type TemplateImportPreviewDto = {
  templateName: string | null;
  description: string | null;
  pages: { page: number; questions: TemplateImportPreviewQuestionDto[] }[];
};

export type TemplateImportPreviewModel = {
  templateName: string | null;
  description: string | null;
  questionsCount: number;
  pagesCount: number;
};

export type ProjectTemplateModel = {
  id: number;
  name: string;
  description?: string | null;
  createdAt: Dayjs;
  createdBy: string;
  updatedAt: Dayjs;
  updatedBy: string;
  categories: CategoryModel[];
  pages: PageModel[];
  kind: ProjectKind;
};

export function mapProjectTemplateModelToDto(model: ProjectTemplateModel): ProjectTemplateDto {
  return {
    id: model.id,
    name: model.name,
    description: model.description,
    createdAt: model.createdAt && model.createdAt.format('YYYY-MM-DD HH:mm:ss'),
    updatedAt: model.updatedAt && model.updatedAt.format('YYYY-MM-DD HH:mm:ss'),
    createdBy: model.createdBy,
    updatedBy: model.updatedBy,
    kind: model.kind,
    categories: model.categories,
  };
}

export function mapProjectTemplateDtoToModel(dto: ProjectTemplateDto): ProjectTemplateModel {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description ?? null,
    createdAt: dayjs.utc(dto.createdAt, 'YYYY-MM-DD HH:mm:ss'),
    updatedAt: dayjs.utc(dto.updatedAt, 'YYYY-MM-DD HH:mm:ss'),
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
    kind: dto.kind,
    categories: dto.categories || [],
    pages: (dto.pages || []).map((pagedto) => mapPageDtoToModel(pagedto)),
  };
}

export function mapTemplateImportPreviewDtoToModel(
  dto: TemplateImportPreviewDto,
): TemplateImportPreviewModel {
  const pages = dto.pages ?? [];

  return {
    templateName: dto.templateName ?? null,
    description: dto.description ?? null,
    questionsCount: pages.reduce((count, page) => count + (page.questions?.length ?? 0), 0),
    pagesCount: pages.length,
  };
}

export namespace ProjectTemplateModel {
  export function isCAS(template: ProjectTemplateModel): boolean {
    return template.kind === ProjectKind.PROJECT_CLIENT_ADVOCACY_SCORE;
  }

  export function isEngagementScore(template: ProjectTemplateModel): boolean {
    return template.kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE;
  }

  export function getPageByPageItem(
    template: ProjectTemplateModel,
    item: PageItemModel,
  ): PageModel {
    return (template.pages || []).find((page) => page.items.includes(item));
  }

  export function getPrevPagesByPageItem(
    template: ProjectTemplateModel,
    item: PageItemModel,
  ): PageModel[] {
    const currentPage = getPageByPageItem(template, item);
    return (template.pages || []).filter((page) => page.position < currentPage.position);
  }

  export function getNextPagesByPageItem(
    template: ProjectTemplateModel,
    item: PageItemModel,
  ): PageModel[] {
    const currentPage = getPageByPageItem(template, item);
    return (template.pages || []).filter((page) => page.position > currentPage.position);
  }
}
